import { Injectable } from '@angular/core';
import { capSQLiteChanges, capSQLiteValues } from '@capacitor-community/sqlite';
import { Boondock, IBoondock } from '@curbnturf/entities';
import { DateTime } from 'luxon';
import { firstValueFrom, of } from 'rxjs';
import { Logger } from '../../log/logger';
import { DatabaseService } from '../database.service';
import { IDbSet } from '../dbset.interface';
import { OfflineBoondockEntity } from '../entities';

const TABLE_NAME = 'offline_boondocks';

@Injectable({
  providedIn: 'root',
})
export class OfflineBoondockDbSet implements IDbSet {
  public static readonly boondockFields = ['sync_id', 'id', 'type', 'body_content', 'user_id'];

  constructor(private databaseService: DatabaseService, private logger: Logger) {}

  public store(request: IBoondock): Promise<capSQLiteValues> {
    const entity = this.mapObjectsToStorable([request]).pop();
    if (entity) {
      return this.databaseService.upsert<OfflineBoondockEntity>(
        TABLE_NAME,
        entity,
        ['type', 'body_content', 'user_id'],
        'sync_id',
      );
    } else {
      throw new Error('Unable to create storable object.');
    }
  }

  public storeArray(requests: IBoondock[], ignoreDuplicates: boolean = false): Promise<capSQLiteValues> {
    const mappedObjects = this.mapObjectsToStorable(requests);
    return this.databaseService.insertMulti<OfflineBoondockEntity>(
      TABLE_NAME,
      mappedObjects,
      OfflineBoondockDbSet.boondockFields,
      ignoreDuplicates,
    );
  }

  public async truncateAndStoreArray(records: IBoondock[]): Promise<capSQLiteValues> {
    const entities = this.mapObjectsToStorable(records);
    await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
    const { statement, values } = this.databaseService.generateInsertStatement<OfflineBoondockEntity>(
      TABLE_NAME,
      entities,
      OfflineBoondockDbSet.boondockFields,
    );
    this.logger.debug('Executing INSERT STATEMENT', statement);
    return this.databaseService.query(statement, values);
  }

  public async truncate(): Promise<capSQLiteChanges> {
    return await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
  }

  public async retrieve(sync_id: string): Promise<Boondock> {
    const results = await this.databaseService.select(TABLE_NAME, { sync_id });
    const objResults = this.mapResultsObject(results);
    if (objResults && objResults?.length > 0) {
      return objResults[0];
    } else {
      return new Boondock();
    }
  }

  public async retrieveOwnBoondocks(userId?: number, type?: string): Promise<Boondock[]> {
    if (userId) {
      let filters: { user_id: number; type?: string } = { user_id: userId };
      if (type) {
        filters = {
          ...filters,
          type: type,
        };
      }
      const results = await this.databaseService.select(TABLE_NAME, filters);
      return this.mapResultsObject(results);
    } else {
      return firstValueFrom(of([]));
    }
  }

  public async retrieveAll(limit?: number, raw: boolean = false): Promise<Boondock[] | OfflineBoondockEntity[]> {
    const results = await this.databaseService.selectAll(TABLE_NAME, undefined, limit);
    if (raw) {
      return results.values as OfflineBoondockEntity[];
    } else {
      return this.mapResultsObject(results);
    }
  }

  public async remove(sync_id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { sync_id });
  }

  private mapResultsObject(results: capSQLiteValues): Boondock[] {
    if (results.values) {
      return results.values?.map((record) => {
        const boondock: IBoondock = JSON.parse(record.body_content);
        return new Boondock({ ...boondock });
      });
    } else {
      return [];
    }
  }

  private mapObjectsToStorable(items: IBoondock[]): OfflineBoondockEntity[] {
    if (items) {
      return items?.map((record: IBoondock) => ({
        // Can't use spread operator here as it tries to enter null in undefined properties as columns
        sync_id: record.syncId || '',
        id: record.id,
        boondock_id: record?.id,
        body_content: JSON.stringify(record),
        last_modified: record.modified || DateTime.now().toMillis(),
      }));
    } else {
      return [];
    }
  }
}
